// Easing functions.
//
// See https://easings.net/ for examples.

export enum EasingDirection {
  IN,
  OUT,
  IN_OUT
}

export enum EasingType {
  LINEAR,
  QUADRATIC,
  CUBIC,
  QUARTIC,
  QUINTIC,
  SINUSOIDAL,
  EXPONENTIAL,
  CIRCULAR,
  ELASTIC,
  BACK,
  BOUNCE
}

// Map of maps of functions.
const easingFunctions = new Map<
  EasingDirection,
  Map<EasingType, (t: number) => number>
>();

const inFunctions = new Map<EasingType, (t: number) => number>();
const outFunctions = new Map<EasingType, (t: number) => number>();
const inOutFunctions = new Map<EasingType, (t: number) => number>();
easingFunctions.set(EasingDirection.IN, inFunctions);
easingFunctions.set(EasingDirection.OUT, outFunctions);
easingFunctions.set(EasingDirection.IN_OUT, inOutFunctions);

inFunctions.set(EasingType.LINEAR, (t: number) => t);
outFunctions.set(EasingType.LINEAR, (t: number) => t);
inOutFunctions.set(EasingType.LINEAR, (t: number) => t);

inFunctions.set(EasingType.QUADRATIC, (t: number) => t * t);
outFunctions.set(EasingType.QUADRATIC, (t: number) => 1 - (1 - t) * (1 - t));
inOutFunctions.set(EasingType.QUADRATIC, (t: number) =>
  t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
);

inFunctions.set(EasingType.CUBIC, (t: number) => t * t * t);
outFunctions.set(EasingType.CUBIC, (t: number) => 1 - Math.pow(1 - t, 3));
inOutFunctions.set(EasingType.CUBIC, (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
);

inFunctions.set(EasingType.QUARTIC, (t: number) => t * t * t * t);
outFunctions.set(EasingType.QUARTIC, (t: number) => 1 - Math.pow(1 - t, 4));
inOutFunctions.set(EasingType.QUARTIC, (t: number) =>
  t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2
);

inFunctions.set(EasingType.QUINTIC, (t: number) => t * t * t * t * t);
outFunctions.set(EasingType.QUINTIC, (t: number) => 1 - Math.pow(1 - t, 5));
inOutFunctions.set(EasingType.QUINTIC, (t: number) =>
  t < 0.5 ? 16 * t * t * t * t * t : 1 - Math.pow(-2 * t + 2, 5) / 2
);

inFunctions.set(EasingType.SINUSOIDAL, (t: number) => 1 - Math.cos((t * Math.PI) / 2));
outFunctions.set(EasingType.SINUSOIDAL, (t: number) => Math.sin((t * Math.PI) / 2));
inOutFunctions.set(
  EasingType.SINUSOIDAL,
  (t: number) => -(Math.cos(Math.PI * t) - 1) / 2
);

inFunctions.set(EasingType.EXPONENTIAL, (t: number) =>
  t === 0 ? 0 : Math.pow(2, 10 * t - 10)
);
outFunctions.set(EasingType.EXPONENTIAL, (t: number) =>
  t === 1 ? 1 : 1 - Math.pow(2, -10 * t)
);
inOutFunctions.set(EasingType.EXPONENTIAL, (t: number) =>
  t === 0
    ? 0
    : t === 1
      ? 1
      : t < 0.5
        ? Math.pow(2, 20 * t - 10) / 2
        : (2 - Math.pow(2, -20 * t + 10)) / 2
);

inFunctions.set(EasingType.CIRCULAR, (t: number) => 1 - Math.sqrt(1 - Math.pow(t, 2)));
outFunctions.set(EasingType.CIRCULAR, (t: number) => Math.sqrt(1 - Math.pow(t - 1, 2)));
inOutFunctions.set(EasingType.CIRCULAR, (t: number) =>
  t < 0.5
    ? (1 - Math.sqrt(1 - Math.pow(2 * t, 2))) / 2
    : (Math.sqrt(1 - Math.pow(-2 * t + 2, 2)) + 1) / 2
);

inFunctions.set(EasingType.ELASTIC, (t: number) => {
  const c4 = (2 * Math.PI) / 3;
  return t === 0
    ? 0
    : t === 1
      ? 1
      : -Math.pow(2, 10 * t - 10) * Math.sin((t * 10 - 10.75) * c4);
});
outFunctions.set(EasingType.ELASTIC, (t: number) => {
  const c4 = (2 * Math.PI) / 3;
  return t === 0
    ? 0
    : t === 1
      ? 1
      : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
});
inOutFunctions.set(EasingType.ELASTIC, (t: number) => {
  const c5 = (2 * Math.PI) / 4.5;
  return t === 0
    ? 0
    : t === 1
      ? 1
      : t < 0.5
        ? -(Math.pow(2, 20 * t - 10) * Math.sin((20 * t - 11.125) * c5)) / 2
        : (Math.pow(2, -20 * t + 10) * Math.sin((20 * t - 11.125) * c5)) / 2 + 1;
});

inFunctions.set(EasingType.BACK, (t: number) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return c3 * t * t * t - c1 * t * t;
});
outFunctions.set(EasingType.BACK, (t: number) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
});
inOutFunctions.set(EasingType.BACK, (t: number) => {
  const c1 = 1.70158;
  const c2 = c1 * 1.525;
  return t < 0.5
    ? (Math.pow(2 * t, 2) * ((c2 + 1) * 2 * t - c2)) / 2
    : (Math.pow(2 * t - 2, 2) * ((c2 + 1) * (t * 2 - 2) + c2) + 2) / 2;
});

function easeOutBounce(t: number): number {
  const n1 = 7.5625;
  const d1 = 2.75;

  if (t < 1 / d1) {
    return n1 * t * t;
  } else if (t < 2 / d1) {
    return n1 * (t -= 1.5 / d1) * t + 0.75;
  } else if (t < 2.5 / d1) {
    return n1 * (t -= 2.25 / d1) * t + 0.9375;
  } else {
    return n1 * (t -= 2.625 / d1) * t + 0.984375;
  }
}

inFunctions.set(EasingType.BOUNCE, (t: number) => 1 - easeOutBounce(1 - t));
outFunctions.set(EasingType.BOUNCE, easeOutBounce);
inOutFunctions.set(EasingType.BOUNCE, (t: number) => {
  return t < 0.5
    ? (1 - easeOutBounce(1 - 2 * t)) / 2
    : (1 + easeOutBounce(2 * t - 1)) / 2;
});

export function easing(
  direction: EasingDirection,
  func: EasingType
): (t: number) => number {
  const newLocal = easingFunctions.get(direction);
  if (!newLocal) throw new Error(`Unknown easing direction: ${direction.toString()}`);
  const value = newLocal.get(func);
  if (!value) throw new Error(`Unknown easing function: ${func.toString()}`);
  return value;
}
